import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Download, Search, Bot, FileText, Globe, Layers, BarChart3 } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const frameworkModules = [
  {
    icon: Search,
    title: "GEO Implementation Guide",
    description: "Step-by-step guide to optimizing for ChatGPT, Perplexity, Claude, and other AI search engines",
  },
  {
    icon: Bot,
    title: "AI Search Optimization Checklist",
    description: "100+ point checklist for ensuring your content is AI-search ready",
  },
  {
    icon: FileText,
    title: "Content Structure Templates",
    description: "Proven templates for creating content that ranks in both Google and AI search",
  },
  {
    icon: Globe,
    title: "Citation Building Strategy",
    description: "How to build authority and citations that AI engines trust and reference",
  },
  {
    icon: Layers,
    title: "Technical GEO Setup",
    description: "Schema markup, structured data, and technical optimizations for AI visibility",
  },
  {
    icon: BarChart3,
    title: "Measurement Framework",
    description: "Track your AI search visibility and measure GEO success",
  },
];

const benefits = [
  "Complete GEO implementation playbook",
  "AI search optimization checklist (100+ items)",
  "Content templates for AI discoverability",
  "Citation and authority building guide",
  "Technical setup instructions",
  "Measurement and tracking framework",
];

const relatedInsights = [
  { title: "What is GEO?", slug: "what-is-geo" },
  { title: "How LLM Search Works", slug: "how-llm-search-works" },
  { title: "SEO for ChatGPT", slug: "seo-for-chatgpt" },
  { title: "AI Search vs Google", slug: "ai-search-vs-google" },
];

export default function SearchGrowthFramework() {
  return (
    <Layout>
      <SEO 
        title="The Search Growth Framework | GEO Guide"
        description="Complete framework for dominating both traditional and AI-powered search. GEO implementation guide, optimization checklist, and content templates."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Resources", href: "/resources" },
              { label: "Search Growth Framework" },
            ]}
            className="mb-8"
          />
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
                <Download className="w-3 h-3 mr-1" />
                Free Framework
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                The Search Growth <span className="text-gradient">Framework</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                The complete playbook for dominating search in the AI era. 
                Traditional SEO + GEO combined into one actionable framework.
              </p>

              <ul className="space-y-3 mb-8">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>

              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/search-framework"
                data-cal-namespace="search-framework"
                data-cal-config='{"layout":"month_view"}'
              >
                Get the Framework
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <p className="text-sm text-muted-foreground mt-4">
                Free download. Includes implementation support call.
              </p>
            </AnimatedSection>

            {/* Framework Modules */}
            <AnimatedSection delay={0.2}>
              <div className="bg-card rounded-3xl border border-border p-8">
                <h2 className="text-2xl font-bold mb-6">Framework Modules</h2>
                <div className="space-y-4">
                  {frameworkModules.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      viewport={{ once: true }}
                      className="flex gap-3 p-3 rounded-xl bg-secondary/50"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{item.title}</h3>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Why This Framework */}
      <section className="py-16 border-t border-border">
        <div className="container-wide">
          <AnimatedSection className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">Why You Need This Framework</h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Search is undergoing its biggest shift since Google launched. Companies that adapt will win. 
              Those that don't will become invisible.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: "40% of Search", description: "Will be AI-first by 2025. Are you ready?" },
                { title: "Zero Brands", description: "Are optimizing for AI search engines today" },
                { title: "First Mover Wins", description: "Early optimizers capture disproportionate visibility" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-6 rounded-2xl bg-card border border-border"
                >
                  <div className="text-3xl font-bold text-primary mb-2">{stat.title}</div>
                  <p className="text-muted-foreground">{stat.description}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Related Insights */}
      <section className="py-16 border-t border-border">
        <div className="container-wide">
          <AnimatedSection>
            <h2 className="text-2xl font-bold text-center mb-4">Learn More About GEO</h2>
            <p className="text-center text-muted-foreground mb-12">
              Deep-dive articles on AI search optimization
            </p>
            <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {relatedInsights.map((insight, index) => (
                <Link
                  key={insight.slug}
                  to={`/insights/${insight.slug}`}
                  className="group p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-all"
                >
                  <h3 className="font-medium group-hover:text-primary transition-colors">{insight.title}</h3>
                  <span className="text-sm text-muted-foreground flex items-center mt-2">
                    Read <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Dominate Search in the AI Era
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get the complete framework and start building visibility 
              across both traditional and AI-powered search.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/search-framework"
                data-cal-namespace="search-framework"
                data-cal-config='{"layout":"month_view"}'
              >
                Get the Framework
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full"
                asChild
              >
                <Link to="/tools/ai-seo-geo-audit">
                  Get Free SEO Audit
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
